import { configureApiClient } from '@labflow/api-client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { queryClient } from './lib/query-client';
import { routerFallbacks } from './lib/router-fallbacks';
import { routeTree } from './routeTree.gen';

import './styles.css';

// 개발에서는 Vite 프록시가 `/v1`을 api 서버로 넘기므로 same-origin으로 둔다.
// 배포에서는 웹(Cloudflare Pages)과 API(Lightsail)가 다른 오리진이므로 빌드 시점에
// VITE_API_BASE_URL이 주입된다 (예: https://api.labflow.kr).
// `x-labflow-client`는 비밀이 아니다 — 상태 변경 요청에 CORS preflight를 강제해
// 같은 사이트의 다른 호스트(프리뷰)가 사용자 세션으로 쓰기를 하지 못하게 한다 (E13 §11 · D70).
// `X-Timezone`은 "오늘" 날짜 경계를 서버가 판정하기 위한 것이다 — 클라이언트는 D-day를
// 계산하지 않지만, 어느 시간대의 자정을 기준으로 할지는 알려줘야 한다 (E10 §1.2 · E14 §10).
configureApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'x-labflow-client': 'web',
    'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
  ...routerFallbacks,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('#root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
